import { useEffect, useState } from 'react'
import { Card, Button, Descriptions, Message } from '@arco-design/web-react'
import { useTranslation } from 'react-i18next'
import { doCheckinApi, getCheckinStatusApi } from '@/api/user'
import { useStatusStore } from '@/stores/statusStore'
import { renderQuota } from '@/utils/quota'
import type { User } from '@/types'

interface CheckinAffiliateCardProps {
  user: User | null
}

export default function CheckinAffiliateCard({ user }: CheckinAffiliateCardProps) {
  const { t } = useTranslation()
  const turnstileCheck = useStatusStore((s) => s.status?.turnstile_check)
  const [checked, setChecked] = useState(false)
  const [checkinLoading, setCheckinLoading] = useState(false)
  const [statusLoading, setStatusLoading] = useState(true)

  useEffect(() => {
    setStatusLoading(true)
    getCheckinStatusApi()
      .then((data) => setChecked(!!data.stats?.checked_in_today))
      .catch(() => { /* ignore */ })
      .finally(() => setStatusLoading(false))
  }, [])

  const doCheck = async () => {
    setCheckinLoading(true)
    try {
      const res = await doCheckinApi()
      Message.success(t('personal.checkin.success', { amount: renderQuota(res.quota_awarded) }))
      setChecked(true)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : ''
      if (turnstileCheck && message.toLowerCase().includes('turnstile')) {
        Message.warning(t('personal.checkin.turnstileRequired'))
      } else {
        Message.error(message || t('personal.checkin.failed'))
      }
    } finally {
      setCheckinLoading(false)
    }
  }

  return (
    <Card className="tt-card !rounded-2xl !border-[var(--console-border)]" title={t('personal.checkin.title')}>
      <div className="space-y-4">
        <div>
          <div className="text-sm font-medium mb-2">{t('personal.checkin.daily')}</div>
          <Button
            type="primary"
            loading={checkinLoading}
            disabled={statusLoading || checked}
            onClick={() => void doCheck()}
          >
            {checked ? t('personal.checkin.done') : t('personal.checkin.action')}
          </Button>
        </div>
        <div>
          <div className="text-sm font-medium mb-2">{t('personal.checkin.affiliate')}</div>
          <Descriptions
            column={2}
            data={[
              { label: t('personal.checkin.affCode'), value: user?.affCode || '-' },
              { label: t('personal.checkin.affCount'), value: String(user?.affCount ?? 0) },
              { label: t('personal.checkin.affAmount'), value: renderQuota(user?.affQuota) },
              { label: t('personal.checkin.affHistory'), value: renderQuota(user?.affHistoryQuota) },
            ]}
          />
        </div>
      </div>
    </Card>
  )
}
